import { useEffect, useRef } from 'react'
import { useTripPlannerEventBusOptional } from 'tripPlanner/assistant/contexts/TripPlannerEventBus'
import { buildOfferDrawerOpenedActivityEntry } from '../components/buildOfferDrawerOpenedActivityEntry'

// Single implementation of "dispatch offer_drawer_opened exactly once per genuine offer-detail
// open," shared by every surface that can show an offer's details (currently HotelDetailView --
// itself shared by ChatOfferDetailDrawer and HotelListingDrawer -- and ExperienceListingDrawer).
//
// Dedupes on offerId, reset when it clears to undefined, rather than relying on the calling
// component's own mount/unmount lifecycle. Some callers unmount between selections
// (HotelDetailView, via HotelListingDrawer's list<->detail toggle) and some don't
// (ExperienceListingDrawer's root, whose child ExperienceDetailView unmounts but whose own dedupe
// state previously didn't). Inferring correctness from "does this specific caller happen to
// unmount" is exactly what let those two callers' hand-copied versions of this logic silently
// diverge -- the hotel path got it right by accident of its lifecycle, the experience path didn't.
// Resetting explicitly when offerId clears makes this correct for both kinds of caller uniformly,
// and for any future one, without depending on lifecycle timing at all.
export function useOfferDrawerOpenedDispatch(
  offerId: string | undefined,
  offerName: string | undefined,
  offerType: string,
): void {
  const eventBus = useTripPlannerEventBusOptional()
  const dispatchedIdRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!offerId) {
      dispatchedIdRef.current = undefined
      return
    }
    // offerName not yet resolved -- the offer detail request is still in flight. Left un-reset
    // (not clearing dispatchedIdRef here) so a slow resolve landing after an unrelated re-render
    // doesn't reopen the window for a duplicate dispatch once it does land.
    if (!offerName || dispatchedIdRef.current === offerId) return
    dispatchedIdRef.current = offerId
    eventBus?.dispatch({
      type: 'trip_planner_surface_activity',
      entries: [buildOfferDrawerOpenedActivityEntry(offerName, offerType, offerId)],
    })
  }, [offerId, offerName, offerType, eventBus])
}
